import { useState } from 'react';

import { Button, Group, Modal, Stack, Text, Textarea } from '@mantine/core';

interface DismissDialogProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void> | void;
  itemTitle?: string;
}

/**
 * Dismiss = "this is not actionable / false positive". Reason is
 * optional; when given it lands in the audit log as resolution notes.
 */
export function DismissDialog({
  opened,
  onClose,
  onConfirm,
  itemTitle,
}: Readonly<DismissDialogProps>) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = !submitting;

  async function handleConfirm() {
    if (!canSubmit) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(reason.trim());
      setReason('');
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={() => {
        if (!submitting) {
          onClose();
        }
      }}
      title="Dismiss item"
      centered
      size="md"
    >
      <Stack gap="sm">
        {itemTitle && (
          <Text size="sm" c="dimmed">
            {itemTitle}
          </Text>
        )}
        <Textarea
          label="Reason (optional)"
          minRows={3}
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          placeholder="Why is this not actionable?"
        />
        {error && (
          <Text size="sm" c="red">
            {error}
          </Text>
        )}
        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button color="red" onClick={handleConfirm} disabled={!canSubmit} loading={submitting}>
            Dismiss
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
